/**
 * ThinkingIndicator - Modern animated thinking/loading indicator
 * 
 * A sophisticated loading animation featuring orbiting particles,
 * shimmer text, and activity bar in Kal's color palette.
 * Designed to feel on-brand and distinct from user messages.
 */
import { useEffect, useState } from 'react';

export function ThinkingIndicator() {
  const [dots, setDots] = useState('');
  
  // Animated ellipsis
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Orbiting particles */}
      <div className="relative flex-shrink-0 w-6 h-6">
        {/* Orbiting particles - exact Kal icon colors */}
        <div className="absolute inset-[-4px] animate-thinking-orbit">
          {/* Rose pink #bb6c80 → Burgundy #8d3f5c */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gradient-to-r from-[#bb6c80] to-[#8d3f5c] shadow-[0_0_10px_rgba(187,108,128,0.9)]" />
        </div>
        <div className="absolute inset-[-4px] animate-thinking-orbit-reverse">
          {/* Gold #dcbc75 → Coral #e0a893 */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#dcbc75] to-[#e0a893] shadow-[0_0_8px_rgba(220,188,117,0.9)]" />
        </div>
        <div className="absolute inset-[-2px] animate-thinking-orbit-slow">
          {/* Teal #409870 */}
          <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#409870] shadow-[0_0_8px_rgba(64,152,112,0.85)]" />
        </div>
      </div>
      
      {/* Text indicator with shimmer - exact Kal icon colors */}
      <div className="flex flex-col justify-center">
        <div className="relative overflow-hidden">
          <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#8d3f5c] via-[#bb6c80] to-[#8d3f5c] bg-[length:200%_100%] animate-thinking-shimmer">
            Thinking{dots}
          </span>
        </div>
        
        {/* Subtle activity bar with Kal colors */}
        <div className="mt-1.5 h-0.5 w-24 rounded-full bg-gradient-to-r from-[#8d3f5c]/10 via-[#bb6c80]/5 to-transparent overflow-hidden">
          <div className="h-full w-8 rounded-full bg-gradient-to-r from-transparent via-[#8d3f5c]/40 to-transparent animate-thinking-progress" />
        </div>
      </div>
    </div>
  );
}

/**
 * ThinkingIndicatorMinimal - Compact version for tight spaces
 */
export function ThinkingIndicatorMinimal() {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="flex gap-1">
        {/* Exact Kal icon colors */}
        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#bb6c80] to-[#8d3f5c] animate-thinking-dot-1" />
        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#dcbc75] to-[#e0a893] animate-thinking-dot-2" />
        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#409870] to-[#99b06f] animate-thinking-dot-3" />
      </div>
    </div>
  );
}

